import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Phone, Home as HomeIcon, Wrench, Building2, DraftingCompass, Paintbrush, Factory, Droplets, PaintRoller, Layers } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { SafeImage } from "@/components/SafeImage";
import { BrandMarquee } from "@/components/Marquee";
import { CATEGORIES, BRANDS, VALUE_PROPS, WHY_CLB, AUDIENCES, BUSINESS_MODEL, FEATURED, PLUMBING_CONSTRUCTION_ITEMS, IMAGES } from "@/data/content";
import { useConfig } from "@/context/ConfigContext";
import { waLink, telLink } from "@/config/site";
import { usePageMeta } from "@/hooks/usePageMeta";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const ICONS = { Home: HomeIcon, Wrench, Building2, DraftingCompass, Paintbrush, Factory };

function Hero() {
  const cfg = useConfig();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden bg-darknavy" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <SafeImage src={IMAGES.hero} alt="Modern Indian home interior finished with products from Chhannulal & Brothers" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-darknavy/60" />
        <div className="absolute inset-x-0 bottom-0 h-56 bg-[linear-gradient(to_top,rgba(11,31,59,0.9),transparent)]" />
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-28 pt-40 lg:px-8">
        <MaskedLine delay={0.15}>
          <p className="flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.4em] text-gold">
            <span className="inline-block h-px w-10 bg-gold" aria-hidden="true" />
            Chhannulal &amp; Brothers · CLB
          </p>
        </MaskedLine>
        <h1 className="mt-8 font-display text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-6xl lg:text-7xl" data-testid="hero-headline">
          <MaskedLine delay={0.3}>Everything You Need to</MaskedLine>
          <MaskedLine delay={0.45}><span className="font-medium">Build, Finish</span> &amp; Upgrade</MaskedLine>
          <MaskedLine delay={0.6}>Your Home<span className="text-gold">.</span></MaskedLine>
        </h1>
        <MaskedLine delay={0.8}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-cream/80 sm:text-lg">
            From paints and plumbing to bathware, water storage and construction solutions — Chhannulal &amp; Brothers brings trusted brands together under one roof.
          </p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/categories" data-testid="hero-explore-products-cta" className="group flex items-center gap-3 bg-cream px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-darknavy transition-colors duration-300 hover:bg-gold">
            Explore Products
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/contact" data-testid="hero-contact-cta" className="flex items-center gap-3 border border-cream/40 px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:border-gold hover:text-gold">
            Contact Us
          </Link>
          <a href={waLink(cfg)} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta" className="flex items-center gap-2 text-cream/80 transition-colors duration-300 hover:text-[#25D366]">
            <WhatsAppIcon className="h-5 w-5" />
            <span className="font-display text-sm font-medium uppercase tracking-[0.15em]">WhatsApp</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="bg-cream py-24 lg:py-28" data-testid="trust-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Overline>Why customers choose us</Overline>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-light tracking-tight text-darknavy sm:text-5xl">
            Trusted Brands. <span className="font-medium text-navy">Complete Solutions.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden border border-darknavy/10 bg-darknavy/10 sm:grid-cols-2 lg:grid-cols-5">
          {VALUE_PROPS.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08} className="bg-cream">
              <div className="group h-full p-7 transition-colors duration-500 hover:bg-white">
                <span className="block h-px w-8 bg-gold transition-all duration-500 group-hover:w-16" aria-hidden="true" />
                <h3 className="mt-5 font-display text-lg font-medium text-darknavy">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-darknavy/60">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const BENTO = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-12"];

function CategoryCard({ c, span, i }) {
  return (
    <Reveal delay={(i % 3) * 0.1} className={span}>
      <Link
        to={`/categories/${c.slug}`}
        data-testid={`category-card-${c.slug}`}
        className="group relative block h-72 overflow-hidden bg-navy md:h-80"
      >
        <SafeImage src={c.image} alt={`${c.name} — Chhannulal & Brothers`} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
        <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(11,31,59,0.92)_0%,rgba(11,31,59,0.25)_55%,transparent)]" />
        <div className="absolute inset-x-0 bottom-0 p-7">
          <p className="font-display text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">{String(i + 1).padStart(2, "0")}</p>
          <h3 className="mt-2 font-display text-2xl font-medium tracking-tight text-cream">{c.name}</h3>
          <p className="mt-2 max-w-md text-sm text-cream/70">{c.short}</p>
          <span className="mt-4 inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 group-hover:text-gold">
            Explore Category
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

function CategoriesSection() {
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="categories-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>What we stock</Overline>
            <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-5xl">Explore Our Categories</h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-darknavy/60">
            Eight categories covering your project from the first pipe to the final coat of paint.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-12">
          {CATEGORIES.map((c, i) => (
            <CategoryCard key={c.slug} c={c} span={BENTO[i]} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BrandsSection() {
  return (
    <section className="bg-cream py-24 lg:py-32" data-testid="brands-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Partners in quality</Overline>
            <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-5xl">Brands You Can Trust</h2>
          </div>
          <Link to="/brands" className="group flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-[0.15em] text-navy transition-colors hover:text-aqua" data-testid="view-all-brands-link">
            All Brands <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 gap-px border border-darknavy/10 bg-darknavy/10 sm:grid-cols-3 lg:grid-cols-5">
          {BRANDS.map((b, i) => (
            <Reveal key={b.name} delay={(i % 5) * 0.06} className="bg-cream">
              <div className="group flex h-28 flex-col items-center justify-center px-4 text-center transition-colors duration-500 hover:bg-white">
                <span className="font-display text-base font-semibold tracking-wide text-navy transition-colors duration-300 group-hover:text-aqua sm:text-lg">{b.name}</span>
                <span className="mt-1 text-[10px] uppercase tracking-[0.2em] text-darknavy/40">{b.category}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="why-clb-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <Overline>The CLB difference</Overline>
                <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-5xl">
                  Why Chhannulal <span className="font-medium">&amp; Brothers?</span>
                </h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-darknavy/65">
                  A family-run business that treats your project like its own — across distribution, wholesale and retail.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7">
            {WHY_CLB.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.05}>
                <div className="group flex gap-6 border-t border-darknavy/10 py-8 transition-colors duration-500 hover:bg-cream/60 sm:gap-10 sm:px-4">
                  <span className="font-display text-3xl font-light text-gold sm:text-4xl">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-display text-xl font-medium text-darknavy sm:text-2xl">{w.title}</h3>
                    <p className="mt-2 max-w-lg text-sm leading-relaxed text-darknavy/60 sm:text-base">{w.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-darknavy/10" />
          </div>
        </div>
      </div>
    </section>
  );
}

function AudiencesSection() {
  return (
    <section className="bg-cream py-24 lg:py-32" data-testid="audiences-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Overline>Who we serve</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-5xl">Solutions for Every Project</h2>
        </Reveal>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {AUDIENCES.map((a, i) => {
            const Icon = ICONS[a.icon];
            return (
              <Reveal key={a.title} delay={(i % 3) * 0.1}>
                <div className="group h-full border border-darknavy/10 bg-white p-8 transition-all duration-500 hover:border-navy hover:shadow-[0_20px_50px_rgba(4,57,94,0.12)]" data-testid={`audience-card-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <Icon className="h-7 w-7 text-navy transition-colors duration-300 group-hover:text-aqua" />
                  <h3 className="mt-5 font-display text-xl font-medium text-darknavy">{a.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-darknavy/60">{a.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const cfg = useConfig();
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="featured-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Selected ranges</Overline>
            <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-5xl">Featured Collections</h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-darknavy/60">
            A closer look at the ranges our customers ask for most. Enquire and we'll guide you to the right fit.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-12">
          {FEATURED.map((f, i) => (
            <Reveal key={f.name} delay={(i % 2) * 0.1} className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : "md:col-span-7"}>
              <article className="group flex h-full flex-col border border-darknavy/10 bg-cream" data-testid={`featured-${f.category}`}>
                <div className="relative h-60 overflow-hidden">
                  <SafeImage src={f.image} alt={`${f.name} by ${f.brand}`} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <span className="absolute left-5 top-5 bg-darknavy/85 px-3 py-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.25em] text-cream backdrop-blur">{f.categoryLabel}</span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <p className="font-display text-[11px] font-semibold uppercase tracking-[0.25em] text-aqua">{f.brand}</p>
                  <h3 className="mt-2 font-display text-2xl font-medium tracking-tight text-darknavy">{f.name}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-darknavy/60">{f.text}</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a href={waLink(cfg, `Hello Chhannulal & Brothers, I would like to enquire about the ${f.name} (${f.brand}).`)} target="_blank" rel="noopener noreferrer" data-testid={`featured-enquire-${f.category}`} className="flex items-center gap-2 bg-navy px-5 py-3 font-display text-xs font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-aqua">
                      Enquire Now <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                    <Link to={`/categories/${f.category}`} className="flex items-center gap-2 border border-navy/30 px-5 py-3 font-display text-xs font-semibold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:border-gold hover:text-gold">
                      View Category
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BathwareShowcase() {
  return (
    <section className="bg-darknavy py-24 text-cream lg:py-32" data-testid="bathware-showcase">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline dark>Luxury Bathware</Overline>
              <h2 className="mt-4 font-display text-3xl font-light tracking-tight sm:text-5xl">
                Transform Your <span className="font-medium text-gold">Bathroom</span>
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-cream/70">
                Bathtubs, jacuzzis and jetted baths, sanitaryware and bathroom fittings — premium bathware that turns a daily routine into a ritual.
              </p>
              <p className="mt-6 font-display text-sm font-medium uppercase tracking-[0.25em] text-cream/50">
                Somany Bathware <span className="mx-2 text-gold">|</span> Cubix <span className="mx-2 text-gold">|</span> Bathberry
              </p>
              <Link to="/categories/luxury-bathware" data-testid="explore-bathware-cta" className="group mt-8 inline-flex items-center gap-3 border border-gold px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-gold transition-colors duration-300 hover:bg-gold hover:text-darknavy">
                Explore Bathware
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -left-4 -top-4 h-full w-full border border-gold/40" aria-hidden="true" />
                <div className="relative h-80 overflow-hidden sm:h-[28rem]">
                  <SafeImage src={IMAGES.bathwareDark} alt="Luxury bathroom with premium bathware from Somany, Cubix and Bathberry" className="h-full w-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function PaintsShowcase() {
  return (
    <section className="bg-cream py-24 lg:py-32" data-testid="paints-showcase">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="order-2 lg:order-1 lg:col-span-7">
            <Reveal>
              <div className="relative">
                <div className="absolute -bottom-4 -right-4 h-full w-full border border-navy/25" aria-hidden="true" />
                <div className="relative h-80 overflow-hidden sm:h-[28rem]">
                  <SafeImage src={IMAGES.paintInterior} alt="Interior walls finished with decorative paints from Nerolac, Dulux, Indigo and JK Paints" className="h-full w-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="order-1 lg:order-2 lg:col-span-5">
            <Reveal delay={0.1}>
              <Overline>Decorative Paints</Overline>
              <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-5xl">
                Bring Your Walls <span className="font-medium text-navy">to Life</span>
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-darknavy/65">
                Interior and exterior paints, primers and finishes — with guidance on the right product for your surface, your light and your budget.
              </p>
              <p className="mt-6 font-display text-sm font-medium uppercase tracking-[0.25em] text-darknavy/50">
                Nerolac <span className="mx-2 text-gold">|</span> Dulux <span className="mx-2 text-gold">|</span> Indigo <span className="mx-2 text-gold">|</span> JK Paints
              </p>
              <div className="mt-6 flex gap-2" aria-hidden="true">
                {["#04395E", "#0E87CC", "#C6A75E", "#B65B3E", "#5E7153"].map((c) => (
                  <span key={c} className="h-8 w-8 border border-darknavy/10" style={{ backgroundColor: c }} />
                ))}
              </div>
              <Link to="/categories/decorative-paints" data-testid="explore-paints-cta" className="group mt-8 inline-flex items-center gap-3 bg-navy px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-aqua">
                Explore Paint Solutions
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function PlumbingConstruction() {
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="plumbing-construction-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Built on the basics</Overline>
              <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-5xl">
                Plumbing &amp; Construction, <span className="font-medium text-navy">Handled</span>
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-darknavy/65">
                The materials that never make the brochure but decide whether a building lasts. CLB stocks the practical essentials for construction and renovation — so your project moves without delays.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {PLUMBING_CONSTRUCTION_ITEMS.map((item) => (
                  <span key={item} className="border border-navy/25 px-4 py-2 font-display text-xs font-medium uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:border-aqua hover:text-aqua">
                    {item}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-5">
                <div className="h-64 overflow-hidden sm:h-80">
                  <SafeImage src={IMAGES.blueprint} alt="Construction planning and building materials at Chhannulal & Brothers" className="h-full w-full object-cover" />
                </div>
                <div className="mt-10 h-64 overflow-hidden sm:h-80">
                  <SafeImage src={CATEGORIES[1].image} alt="Pipes and fittings installation" className="h-full w-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function BusinessModelSection() {
  return (
    <section className="border-y border-darknavy/10 bg-cream py-24 lg:py-28" data-testid="business-model-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Overline>How we work</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy sm:text-5xl">Distribution · Wholesale · Retail</h2>
        </Reveal>
        <div className="mt-14 grid gap-px border border-darknavy/10 bg-darknavy/10 md:grid-cols-3">
          {BUSINESS_MODEL.map((m, i) => (
            <Reveal key={m.num} delay={i * 0.1} className="bg-cream">
              <div className="h-full p-8 transition-colors duration-500 hover:bg-white lg:p-10">
                <span className="font-display text-4xl font-light text-gold">{m.num}</span>
                <h3 className="mt-5 font-display text-2xl font-medium text-darknavy">{m.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-darknavy/60">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const cfg = useConfig();
  return (
    <section className="bg-navy py-24 text-cream lg:py-28" data-testid="final-cta-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="flex flex-wrap items-center justify-between gap-10">
          <div className="max-w-xl">
            <h2 className="font-display text-3xl font-light tracking-tight sm:text-5xl">
              Tell us what you're <span className="font-medium text-gold">building.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-cream/70">
              Share your requirement on WhatsApp, call us, or send an enquiry — we'll help you find the right products from brands you can trust.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <a href={waLink(cfg)} target="_blank" rel="noopener noreferrer" data-testid="final-whatsapp-cta" className="flex items-center gap-2 bg-[#25D366] px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-[1.03]">
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp Us
            </a>
            <a href={telLink(cfg)} data-testid="final-call-cta" className="flex items-center gap-2 border border-cream/40 px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:border-gold hover:text-gold">
              <Phone className="h-4 w-4" /> Call Us
            </a>
            <Link to="/contact" data-testid="final-enquiry-cta" className="flex items-center gap-2 bg-cream px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-darknavy transition-colors duration-300 hover:bg-gold">
              Get a Quote
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta(
    "Chhannulal & Brothers (CLB) | Paints, Plumbing, Bathware & Building Materials",
    "Chhannulal & Brothers (CLB) — Your Complete Home Solutions. Trusted brands in decorative paints, pipes & fittings, luxury bathware, water tanks, kitchen sinks and construction chemicals. Retail, wholesale and distribution."
  );
  return (
    <main>
      <Hero />
      <TrustStrip />
      <BrandMarquee />
      <CategoriesSection />
      <BrandsSection />
      <WhySection />
      <AudiencesSection />
      <FeaturedSection />
      <BathwareShowcase />
      <PaintsShowcase />
      <PlumbingConstruction />
      <BusinessModelSection />
      <FinalCTA />
    </main>
  );
}
